import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { useToast } from '../../hooks/use-toast';
import {
  BookOpen,
  Award,
  Clock,
  CheckCircle2,
  Lock,
  Play,
  ChevronRight,
  Trophy,
  Star,
  Mic,
  Shield,
  GraduationCap,
  Target,
  BookMarked,
  FileText
} from 'lucide-react';

const ARCDashboard = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [dashboard, setDashboard] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/arc/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch ARC dashboard:', error);
      toast({
        title: 'Error',
        description: 'Failed to load certification data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [api, toast]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleEnroll = async () => {
    try {
      setEnrolling(true);
      await api.post('/arc/enroll');
      toast({
        title: 'Enrolled!',
        description: 'Welcome to Advanced Recruitment Certification',
      });
      fetchDashboard();
    } catch (error) {
      console.error('Enrollment error:', error);
      toast({
        title: 'Error',
        description: 'Failed to enroll',
        variant: 'destructive'
      });
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-12 w-96" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
          </div>
          <Skeleton className="h-64" />
        </div>
      </MainLayout>
    );
  }

  if (!dashboard) {
    return (
      <MainLayout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Card className="max-w-lg text-center p-8">
            <CardContent>
              <GraduationCap className="h-16 w-16 mx-auto mb-4 text-amber-500" />
              <h2 className="text-2xl font-bold mb-2">Advanced Recruitment Certification</h2>
              <p className="text-muted-foreground mb-6">
                Master the complete art and science of technical recruiting with our comprehensive certification program.
              </p>
              <Button onClick={handleEnroll} disabled={enrolling} size="lg">
                {enrolling ? 'Enrolling...' : 'Start Your Certification Journey'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const { course, progress, certification, modules } = dashboard;

  return (
    <MainLayout>
      <div className="p-6 space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg">
                <Award className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                  {course.title}
                </h1>
                <p className="text-muted-foreground">{course.subtitle}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground max-w-2xl mt-2">
              {course.description}
            </p>
          </div>
          
          {certification.status === 'certified' && (
            <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 text-sm">
              <Trophy className="h-4 w-4 mr-2" />
              ARC Certified
            </Badge>
          )}
        </div>

        {/* Progress Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200 dark:border-amber-800">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <BookOpen className="h-8 w-8 text-amber-600" />
                <div>
                  <p className="text-2xl font-bold">{progress.chapters_completed}/{progress.total_chapters}</p>
                  <p className="text-xs text-muted-foreground">Chapters Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-emerald-200 dark:border-emerald-800">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <Target className="h-8 w-8 text-emerald-600" />
                <div>
                  <p className="text-2xl font-bold">{progress.avg_quiz_score}%</p>
                  <p className="text-xs text-muted-foreground">Avg Quiz Score</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <Clock className="h-8 w-8 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold">{Math.round(progress.total_time_spent_minutes / 60)}h</p>
                  <p className="text-xs text-muted-foreground">Time Invested</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 border-violet-200 dark:border-violet-800">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <GraduationCap className="h-8 w-8 text-violet-600" />
                <div>
                  <p className="text-2xl font-bold">{progress.progress_percentage}%</p>
                  <p className="text-xs text-muted-foreground">Overall Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Overall Progress Bar */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Certification Progress</span>
              <span className="text-sm text-muted-foreground">{progress.progress_percentage}%</span>
            </div>
            <Progress value={progress.progress_percentage} className="h-3" />
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>Start</span>
              <span>Final Exam</span>
              <span>Certified</span>
            </div>
          </CardContent>
        </Card>

        {/* Course Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/20 dark:to-pink-950/20 border-rose-200 dark:border-rose-800">
            <CardContent className="pt-4 flex items-start gap-3">
              <Shield className="h-6 w-6 text-rose-600 mt-1" />
              <div>
                <h3 className="font-semibold">Anti-Piracy Protected</h3>
                <p className="text-xs text-muted-foreground">Content is protected from copying and downloading</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-950/20 dark:to-blue-950/20 border-sky-200 dark:border-sky-800">
            <CardContent className="pt-4 flex items-start gap-3">
              <Mic className="h-6 w-6 text-sky-600 mt-1" />
              <div>
                <h3 className="font-semibold">Voice-Based Quizzes</h3>
                <p className="text-xs text-muted-foreground">Verbal assessments with AI feedback</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20 border-amber-200 dark:border-amber-800">
            <CardContent className="pt-4 flex items-start gap-3">
              <Star className="h-6 w-6 text-amber-600 mt-1" />
              <div>
                <h3 className="font-semibold">Audio Narration</h3>
                <p className="text-xs text-muted-foreground">Listen to each page with AI voice</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Access Cards */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Quick Access</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Course Chapters Card */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-2 border-transparent hover:border-amber-300"
              onClick={() => {
                const firstIncomplete = modules?.flatMap(m => m.chapters).find(ch => !ch.completed);
                if (firstIncomplete) {
                  navigate(`/arc/chapter/${firstIncomplete.id}`);
                } else if (modules?.[0]?.chapters?.[0]) {
                  navigate(`/arc/chapter/${modules[0].chapters[0].id}`);
                }
              }}
              data-testid="quick-access-chapters"
            >
              <CardContent className="pt-6 text-center">
                <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-4">
                  <BookOpen className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-1">Course Chapters</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  {progress.chapters_completed}/{progress.total_chapters} completed
                </p>
                <Button size="sm" className="bg-gradient-to-r from-amber-500 to-orange-600">
                  {progress.chapters_completed > 0 ? 'Continue' : 'Start'} Learning
                </Button>
              </CardContent>
            </Card>

            {/* Playbook Card - NEW */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-2 border-transparent hover:border-emerald-300"
              onClick={() => navigate('/arc/playbook')}
              data-testid="quick-access-playbook"
            >
              <CardContent className="pt-6 text-center">
                <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-4">
                  <BookMarked className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-1">Full Playbook</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Complete 500+ page reference guide
                </p>
                <Button size="sm" variant="outline" className="border-emerald-500 text-emerald-600 hover:bg-emerald-50">
                  <FileText className="h-4 w-4 mr-2" />
                  Read Book
                </Button>
              </CardContent>
            </Card>

            {/* Final Exam Card */}
            <Card 
              className={`cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-2 ${
                progress.progress_percentage >= 100 
                  ? 'border-transparent hover:border-violet-300' 
                  : 'border-dashed opacity-75'
              }`}
              onClick={() => progress.progress_percentage >= 100 && navigate('/arc/final-exam')}
              data-testid="quick-access-final-exam"
            >
              <CardContent className="pt-6 text-center">
                <div className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4 ${
                  progress.progress_percentage >= 100 
                    ? 'bg-gradient-to-br from-violet-500 to-purple-600' 
                    : 'bg-muted'
                }`}>
                  {progress.progress_percentage >= 100 ? (
                    <Trophy className="h-7 w-7 text-white" />
                  ) : (
                    <Lock className="h-7 w-7 text-muted-foreground" />
                  )}
                </div>
                <h3 className="font-bold text-lg mb-1">Final Exam</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  {progress.progress_percentage >= 100 
                    ? '30 verbal questions' 
                    : `Complete all chapters to unlock`
                  }
                </p>
                <Button 
                  size="sm" 
                  disabled={progress.progress_percentage < 100}
                  className={progress.progress_percentage >= 100 ? 'bg-gradient-to-r from-violet-500 to-purple-600' : ''}
                >
                  {progress.progress_percentage >= 100 ? 'Take Exam' : 'Locked'}
                </Button>
              </CardContent>
            </Card>

            {/* Certificate Card */}
            <Card 
              className={`cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-2 ${
                certification.status === 'certified' 
                  ? 'border-transparent hover:border-amber-400 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20' 
                  : 'border-dashed opacity-75'
              }`}
              onClick={() => certification.status === 'certified' && navigate('/arc/certificate')}
              data-testid="quick-access-certificate"
            >
              <CardContent className="pt-6 text-center">
                <div className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4 ${
                  certification.status === 'certified' 
                    ? 'bg-gradient-to-br from-amber-400 to-yellow-500' 
                    : 'bg-muted'
                }`}>
                  {certification.status === 'certified' ? (
                    <Award className="h-7 w-7 text-white" />
                  ) : (
                    <Lock className="h-7 w-7 text-muted-foreground" />
                  )}
                </div>
                <h3 className="font-bold text-lg mb-1">Certificate</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  {certification.status === 'certified' 
                    ? 'Download your certificate' 
                    : 'Pass final exam to earn'
                  }
                </p>
                <Button 
                  size="sm" 
                  disabled={certification.status !== 'certified'}
                  className={certification.status === 'certified' ? 'bg-gradient-to-r from-amber-400 to-yellow-500' : ''}
                >
                  {certification.status === 'certified' ? 'View Certificate' : 'Locked'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Modules & Chapters */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Course Modules</h2>
          <div className="space-y-6">
            {modules.map((module) => (
              <Card key={module.id} className="overflow-hidden">
                <CardHeader className="pb-2 bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-900/50 dark:to-gray-900/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge variant="outline" className="mb-2">Module {module.number}</Badge>
                      <CardTitle className="text-lg">{module.title}</CardTitle>
                      <CardDescription>{module.description}</CardDescription>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {module.chapters.filter(ch => ch.completed).length}/{module.chapters.length}
                      </p>
                      <p className="text-xs text-muted-foreground">chapters</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {module.chapters.map((chapter) => (
                      <div
                        key={chapter.id}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer hover:shadow-md ${
                          chapter.completed 
                            ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800' 
                            : 'bg-white dark:bg-slate-900 hover:border-amber-300'
                        }`}
                        onClick={() => navigate(`/arc/chapter/${chapter.id}`)}
                        data-testid={`chapter-${chapter.id}`}
                      >
                        <div className="flex items-center gap-3">
                          {chapter.completed ? (
                            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                          ) : (
                            <Play className="h-5 w-5 text-amber-600" />
                          )}
                          <div>
                            <p className="font-medium text-sm">
                              Chapter {chapter.number}: {chapter.title}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {chapter.page_count} pages • {chapter.quiz_count} quiz questions
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {chapter.quiz_score !== null && (
                            <Badge variant={chapter.quiz_score >= 70 ? 'default' : 'secondary'} className="text-xs">
                              {Math.round(chapter.quiz_score)}%
                            </Badge>
                          )}
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Final Exam Section */}
        <Card className={`border-2 ${
          progress.progress_percentage >= 100 
            ? 'border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30' 
            : 'border-dashed'
        }`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-4 rounded-xl ${
                  progress.progress_percentage >= 100 
                    ? 'bg-gradient-to-br from-amber-500 to-orange-600 text-white' 
                    : 'bg-muted'
                }`}>
                  <Trophy className="h-8 w-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Final Certification Exam</h3>
                  <p className="text-sm text-muted-foreground">
                    {progress.progress_percentage >= 100 
                      ? '30 verbal questions covering all modules' 
                      : `Complete all ${progress.total_chapters} chapters to unlock`
                    }
                  </p>
                  {certification.final_exam_score !== null && (
                    <p className="text-sm mt-1">
                      Best Score: <span className="font-semibold">{Math.round(certification.final_exam_score)}%</span>
                      {' • '}Attempts: {certification.final_exam_attempts}/{certification.max_attempts}
                    </p>
                  )}
                </div>
              </div>
              <Button 
                size="lg"
                disabled={progress.progress_percentage < 100}
                onClick={() => navigate('/arc/final-exam')}
                className={progress.progress_percentage >= 100 ? 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700' : ''}
                data-testid="start-final-exam"
              >
                {progress.progress_percentage >= 100 ? (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Start Exam
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Locked
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ARCDashboard;
